import { MutableRefObject } from 'react';
import type { Extensions } from '@tiptap/core';

import { addUniqueIdAttribute } from '../../extensions/CustomUniqueId';
import DropdownOptionsTransport from '../../extensions/DropdownOptionsTransport';
import type { TTransformFragment } from '../../extensions/PasteRule';
import FormFieldLineGuard from '../../extensions/FormFieldLineGuard';
import ParagraphFieldCaret from '../../extensions/ParagraphFieldCaret';
import SectionNode from '../section/SectionNode';
import FormFieldNode from '../formField/FormFieldNode';
import FormFieldLineNode from '../formField/FormFieldLineNode';
import TemplateFieldInlineNode from './TemplateFieldInlineNode';
import TemplateFieldBlockNode from './TemplateFieldBlockNode';
import TemplateFieldDropdownNode from './TemplateFieldDropdownNode';
import { withFieldSettings } from './fieldSettings';

type TOptions = {
  usesFormFields: boolean;
  isTemplate: boolean;
  optionsRef: MutableRefObject<Record<string, string[]>>;
  copyFields: TTransformFragment;
};

/** The landscape section and the template fields of an editor, as form fields or as plain fields. */
const getTemplateFieldNodes = ({ usesFormFields, isTemplate, optionsRef, copyFields }: TOptions): Extensions => {
  const fields = [
    TemplateFieldInlineNode,
    TemplateFieldBlockNode,
    TemplateFieldDropdownNode.configure({ isTemplate, refTemplateDropdownOptions: optionsRef }),
  ];
  if (!usesFormFields) return [SectionNode, ...fields].map(addUniqueIdAttribute);

  return [
    // Extended here, so the standalone Section node keeps its own schema.
    addUniqueIdAttribute(SectionNode.extend({ content: '(block | formField)+' })),
    // Settings wrap last: every later extend copies a node's hooks, so their calls to the parent would run twice.
    ...fields.map((field) => withFieldSettings(addUniqueIdAttribute(field))),
    ParagraphFieldCaret,
    addUniqueIdAttribute(FormFieldNode.configure({ copyFields })),
    FormFieldLineNode,
    FormFieldLineGuard,
    DropdownOptionsTransport.configure({ optionsRef }),
  ];
};

export default getTemplateFieldNodes;
